import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";
import { postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { TriggerMode } from "../types/trigger_mode";
import { ExportType } from "../types/export_type";
import { Clients } from "../../../lib/clients";

const TriggerSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
});

type TriggerSchemaType = z.infer<typeof TriggerSchema>;

function LookthroughExportActionButton({
  client,
  actionLabel,
  mode,
  exportType,
}: {
  client: "g4" | "e2" | "c3";
  actionLabel: string;
  mode: TriggerMode;
  exportType?: ExportType;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentPage = useCurrentPageFromUrl();
  const { handleSubmit, setValue, watch, register, reset } =
    useForm<TriggerSchemaType>({
      mode: "onChange",
      defaultValues: {
        reportingDate: undefined,
      },
      resolver: zodResolver(TriggerSchema) as Resolver<TriggerSchemaType>,
    });

  const triggerMutation = useMutation({
    mutationFn: async (data: TriggerSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);
      if (!formattedDate) {
        throw new Error("Date du rapport requise");
      }
      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${client}_download_center/run-lookthrough-export`,
        {
          client: client.toUpperCase(),
          prefix: client,
          reportingDate: formattedDate,
          mode,
          export_type: exportType,
          action_button_name: actionLabel,
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success("Traitement lancé");
      setIsOpen(false);
      reset();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail || "Erreur au lancement du traitement";
      toast.error(message);
    },
  });

  const handleFormSubmit = (data: TriggerSchemaType) => {
    triggerMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {actionLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 p-1 w-full"
        >
          <DateField
            name="reportingDate"
            label={t(`button.lookThroughExport.date`, {
              ns: "tables",
            })}
            watch={watch}
            setValue={setValue}
            register={register}
            required={true}
          />

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isLoading={triggerMutation.isPending}
            >
              Lancer
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}

export function G4RunLookthroughButton() {
  const { t } = useTranslation();
  return (
    <LookthroughExportActionButton
      client={Clients.G4}
      mode="lookthrough_only"
      actionLabel={t("reports.generate_lookthrough")}
    />
  );
}

export function G4GenerateTranspaButton() {
  const { t } = useTranslation();
  return (
    <LookthroughExportActionButton
      client={Clients.G4}
      mode="calculate_and_export"
      exportType="g4_transpa"
      actionLabel={t("reports.generate_with_lookthrough_action")}
    />
  );
}

export function G4GenerateEsgConsolideButton() {
  const { t } = useTranslation();
  return (
    <LookthroughExportActionButton
      client={Clients.G4}
      mode="calculate_and_export"
      exportType="g4_esg_consolide"
      actionLabel={t("reports.generate_esg_action")}
    />
  );
}

export function C3RunLookthroughButton() {
  const { t } = useTranslation();
  return (
    <LookthroughExportActionButton
      client={Clients.C3}
      mode="lookthrough_only"
      actionLabel={t("reports.generate_lookthrough")}
    />
  );
}

export function E2RunLookthroughButton() {
  const { t } = useTranslation();
  return (
    <LookthroughExportActionButton
      client={Clients.E2}
      mode="lookthrough_only"
      actionLabel={t("reports.generate_lookthrough")}
    />
  );
}

export function E2GeneratePdfButton() {
  return (
    <LookthroughExportActionButton
      client={Clients.E2}
      mode="calculate_and_export"
      exportType="e2_pdf"
      actionLabel="E2 - Export PDF"
    />
  );
}

export function E2GenerateExcelButton() {
  return (
    <LookthroughExportActionButton
      client={Clients.E2}
      mode="calculate_and_export"
      exportType="e2_excel"
      actionLabel="E2 - Export Excel"
    />
  );
}
